import { useState } from 'react'

import { formatDate, formatRelativeTime } from '#/shared/lib/format'
import { Button } from '#/shared/ui/button'
import { StatusBadge } from '#/shared/ui/status-badge'

import { AddPasskeyDialog, RemovePasskeyDialog, RenamePasskeyDialog } from './passkey-dialogs'
import { useCurrentAdmin } from './api'
import { passkeyDeviceLabel, passkeysSupported, usePasskeys, type Passkey } from './passkeys'
import { RecoveryCodesCard } from './recovery-codes-card'

function PasskeyRow({
  passkey,
  onRename,
  onRemove,
}: {
  passkey: Passkey
  onRename: () => void
  onRemove: () => void
}) {
  const device = passkeyDeviceLabel(passkey)

  return (
    <li className="flex items-center gap-4 px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">{passkey.name}</span>
          <StatusBadge variant={device.variant}>{device.text}</StatusBadge>
        </span>
        <span className="text-label text-gray-500">
          Added {formatDate(passkey.created_at)} ·{' '}
          {passkey.last_used_at
            ? `last used ${formatRelativeTime(passkey.last_used_at)}`
            : 'never used to sign in'}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button size="xs" variant="ghost" onClick={onRename}>
          Rename
        </Button>
        <Button size="xs" variant="ghost" className="text-red-500" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </li>
  )
}

function PasskeyList({
  passkeys,
  loading,
  onRename,
  onRemove,
}: {
  passkeys: Passkey[]
  loading: boolean
  onRename: (passkey: Passkey) => void
  onRemove: (passkey: Passkey) => void
}) {
  if (loading) {
    return <p className="px-4 py-6 text-center text-sm text-gray-500">Loading…</p>
  }
  if (passkeys.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-sm text-gray-500">
        You have no passkeys yet — you sign in with your password only.
      </p>
    )
  }
  return (
    <ul className="divide-y divide-gray-200">
      {passkeys.map((passkey) => (
        <PasskeyRow
          key={passkey.id}
          passkey={passkey}
          onRename={() => onRename(passkey)}
          onRemove={() => onRemove(passkey)}
        />
      ))}
    </ul>
  )
}

const SUPPORTED_NOTE =
  'Sign in with your fingerprint, face or device PIN instead of a password. Add two — for example this laptop and a security key or your phone — so losing one device does not lock you out.'
const UNSUPPORTED_NOTE =
  'This browser cannot use passkeys. Open the console in a current Chrome, Safari, Edge or Firefox to add one.'

/**
 * How the signed-in admin gets into the console.
 *
 * A passkey is tied to this console's address and unlocks with the admin's
 * fingerprint, face or device PIN, so a phished or reused password is not
 * enough to get in. Each admin manages only their own; nothing here can see or
 * touch another admin's passkeys.
 */
export function SecurityPage() {
  const passkeys = usePasskeys()
  const me = useCurrentAdmin()
  const supported = passkeysSupported()
  const [adding, setAdding] = useState(false)
  const [renaming, setRenaming] = useState<Passkey | null>(null)
  const [removing, setRemoving] = useState<Passkey | null>(null)
  const rows = passkeys.data ?? []

  return (
    <div className="flex min-w-0 flex-col gap-4 p-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-title text-gray-900">Security</h1>
        <p className="text-sm text-gray-500">How you sign in to the platform console.</p>
      </header>

      <section className="max-w-3xl rounded-md border border-gray-200 bg-white">
        <header className="flex h-12 items-center justify-between gap-3 rounded-t-md border-b border-gray-200 bg-gray-50 px-4">
          <span className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-gray-900">Passkeys</h2>
            {passkeys.isLoading ? null : (
              <StatusBadge variant={rows.length > 0 ? 'success' : 'warning'}>
                {rows.length > 0 ? `${String(rows.length)} added` : 'none yet'}
              </StatusBadge>
            )}
          </span>
          <Button size="xs" disabled={!supported} onClick={() => setAdding(true)}>
            Add a passkey
          </Button>
        </header>
        <p className="text-label border-b border-gray-200 px-4 py-3 text-gray-500">
          {supported ? SUPPORTED_NOTE : UNSUPPORTED_NOTE}
        </p>
        <PasskeyList
          passkeys={rows}
          loading={passkeys.isLoading}
          onRename={setRenaming}
          onRemove={setRemoving}
        />
      </section>

      {rows.length > 0 && me.data ? <RecoveryCodesCard email={me.data.email} /> : null}

      <AddPasskeyDialog open={adding} onOpenChange={setAdding} />
      <RenamePasskeyDialog passkey={renaming} onClose={() => setRenaming(null)} />
      <RemovePasskeyDialog passkey={removing} onClose={() => setRemoving(null)} />
    </div>
  )
}
